import { useState } from 'react'
import { Alert, Badge, Button, Card, Form, Modal, ProgressBar, Spinner, Table } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { aInputLocal, codigoBloqueado, datosCampana, porcentajeCupo } from '../../utils/campanas'

/**
 * Las campañas del registro público de invitados (cada tarjeta con QR apunta a una).
 *
 * Antes sólo vivían en el Django Admin y era fácil olvidar que existían. Aquí el superadmin ve su
 * cupo, si están abiertas y por qué no, y la liga del QR; las crea, las edita y las desactiva.
 * **No se borran**: sus registros guardan las aceptaciones legales. Las reglas las valida el
 * servidor (`campanas_api.py`); la pantalla sólo las anticipa.
 */
export default function CampanasInvitados({ paquetes }) {
  const queryClient = useQueryClient()
  const [editando, setEditando] = useState(undefined)   // undefined = cerrado, null = nueva
  const [viendo, setViendo] = useState(null)             // la campaña cuyos registrados se ven

  const { data: campanas = [], isLoading } = useQuery(
    'campanas-invitados',
    async () => (await api.get('/auth/campanas/')).data
  )

  const alternar = useMutation(
    (c) => api.patch(`/auth/campanas/${c.id}/`, { activa: !c.activa }),
    {
      onSuccess: () => queryClient.invalidateQueries('campanas-invitados'),
      onError: () => toast.error('No se pudo cambiar el estado de la campaña'),
    }
  )

  const copiar = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Liga copiada')
    } catch {
      toast.info(url)
    }
  }

  return (
    <Card className="mt-2 mb-4" data-testid="campanas-invitados">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Campañas de invitados</strong>
          <div className="small text-muted">
            Cada tarjeta con QR apunta a una campaña: su cupo limita cuántos invitados reciben prueba.
          </div>
        </div>
        <Button size="sm" onClick={() => setEditando(null)}>Nueva campaña</Button>
      </Card.Header>
      <Card.Body className="p-0">
        {isLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : campanas.length === 0 ? (
          <div className="text-center text-muted py-4">Todavía no hay campañas.</div>
        ) : (
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Campaña</th><th>Plan</th><th style={{ minWidth: 160 }}>Cupo</th>
                <th>Estado</th><th>Liga del QR</th><th />
              </tr>
            </thead>
            <tbody>
              {campanas.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="fw-semibold">{c.nombre}</div>
                    <div className="small text-muted">{c.codigo}</div>
                  </td>
                  <td className="small">{c.paquete_nombre}</td>
                  <td>
                    <div className="small">
                      <strong>{c.verificados}</strong> de {c.tope} verificados
                    </div>
                    <ProgressBar now={porcentajeCupo(c)} style={{ height: 6 }}
                                 variant={porcentajeCupo(c) >= 100 ? 'danger' : porcentajeCupo(c) >= 80 ? 'warning' : 'success'} />
                    <div className="small text-muted">
                      {c.pendientes} sin verificar · {c.en_fila} en fila de timbres
                    </div>
                  </td>
                  <td>
                    {c.abierta
                      ? <Badge bg="success">abierta</Badge>
                      : <Badge bg="secondary" title={c.motivo}>cerrada</Badge>}
                    {!c.abierta && <div className="small text-muted">{c.motivo}</div>}
                  </td>
                  <td>
                    <Button variant="link" size="sm" className="p-0" onClick={() => copiar(c.url_qr)}>
                      Copiar liga
                    </Button>
                  </td>
                  <td className="text-end text-nowrap">
                    <Button variant="outline-secondary" size="sm" className="me-1"
                            onClick={() => setViendo(c)}>Registrados</Button>
                    <Button variant="outline-primary" size="sm" className="me-1"
                            onClick={() => setEditando(c)}>Editar</Button>
                    <Button variant={c.activa ? 'outline-danger' : 'outline-success'} size="sm"
                            onClick={() => alternar.mutate(c)}>
                      {c.activa ? 'Desactivar' : 'Activar'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {editando !== undefined && (
        <FormCampana campana={editando} paquetes={paquetes}
                     onHide={() => setEditando(undefined)}
                     onSuccess={() => {
                       setEditando(undefined)
                       queryClient.invalidateQueries('campanas-invitados')
                     }} />
      )}
      {viendo && <Registrados campana={viendo} onHide={() => setViendo(null)} />}
    </Card>
  )
}

function FormCampana({ campana, paquetes, onHide, onSuccess }) {
  const [form, setForm] = useState(() => ({
    codigo: campana?.codigo || '',
    nombre: campana?.nombre || '',
    paquete: campana?.paquete || '',
    tope: campana?.tope ?? 100,
    activa: campana?.activa ?? true,
    inicio: aInputLocal(campana?.inicio),
    fin: aInputLocal(campana?.fin),
  }))
  const [errores, setErrores] = useState({})
  const bloqueado = codigoBloqueado(campana)
  // Ni el plan interno (sin límites) ni los planes retirados de la venta.
  const planes = paquetes.filter((p) => !p.es_interno && (p.activo || p.id === campana?.paquete))

  const guardar = useMutation(
    (datos) => (campana
      ? api.patch(`/auth/campanas/${campana.id}/`, datos)
      : api.post('/auth/campanas/', datos)),
    {
      onSuccess: () => {
        toast.success(campana ? 'Campaña actualizada' : 'Campaña creada')
        onSuccess()
      },
      onError: (e) => setErrores(e?.response?.data || { detail: 'No se pudo guardar' }),
    }
  )

  const cambiar = (campo) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [campo]: v }))
  }
  const error = (campo) => {
    const m = errores[campo]
    return m ? (Array.isArray(m) ? m.join(' ') : String(m)) : null
  }

  const enviar = (e) => {
    e.preventDefault()
    setErrores({})
    const datos = datosCampana(form)
    if (bloqueado) delete datos.codigo
    guardar.mutate(datos)
  }

  return (
    <Modal show onHide={onHide}>
      <Form onSubmit={enviar}>
        <Modal.Header closeButton>
          <Modal.Title>{campana ? 'Editar campaña' : 'Nueva campaña'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errores.detail && <Alert variant="danger">{errores.detail}</Alert>}
          {(errores.non_field_errors || errores.__all__) && (
            <Alert variant="danger">{error('non_field_errors') || error('__all__')}</Alert>
          )}
          <Form.Group className="mb-3" controlId="campanaNombre">
            <Form.Label>Nombre</Form.Label>
            <Form.Control value={form.nombre} onChange={cambiar('nombre')} maxLength={120} required
                          isInvalid={!!error('nombre')} placeholder="Encuentro de Emprendimiento" />
            <Form.Control.Feedback type="invalid">{error('nombre')}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="campanaCodigo">
            <Form.Label>Código</Form.Label>
            <Form.Control value={form.codigo} onChange={cambiar('codigo')} maxLength={40} required
                          disabled={bloqueado} isInvalid={!!error('codigo')} placeholder="evento2027" />
            <Form.Text>
              {bloqueado
                ? 'Ya tiene registros: el código va impreso en el QR y no se cambia.'
                : 'Va en la liga del QR (letras, números y guiones).'}
            </Form.Text>
            <Form.Control.Feedback type="invalid">{error('codigo')}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="campanaPlan">
            <Form.Label>Plan que reciben los invitados</Form.Label>
            <Form.Select value={form.paquete} onChange={cambiar('paquete')} required
                         isInvalid={!!error('paquete')}>
              <option value="">Elige un plan</option>
              {planes.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{error('paquete')}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="campanaTope">
            <Form.Label>Cupo (invitados verificados)</Form.Label>
            <Form.Control type="number" min={campana?.verificados || 1} value={form.tope}
                          onChange={cambiar('tope')} required isInvalid={!!error('tope')} />
            <Form.Text>Al llegar al cupo, la página del QR dice que se agotaron los lugares.</Form.Text>
            <Form.Control.Feedback type="invalid">{error('tope')}</Form.Control.Feedback>
          </Form.Group>
          <div className="d-flex gap-2">
            <Form.Group className="mb-3 flex-fill" controlId="campanaInicio">
              <Form.Label>Abre <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control type="datetime-local" value={form.inicio} onChange={cambiar('inicio')}
                            isInvalid={!!error('inicio')} />
            </Form.Group>
            <Form.Group className="mb-3 flex-fill" controlId="campanaFin">
              <Form.Label>Cierra <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control type="datetime-local" value={form.fin} onChange={cambiar('fin')}
                            isInvalid={!!error('fin')} />
              <Form.Control.Feedback type="invalid">{error('fin')}</Form.Control.Feedback>
            </Form.Group>
          </div>
          <Form.Check id="campanaActiva" label="Activa" checked={form.activa}
                      onChange={cambiar('activa')} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={guardar.isLoading}>
            {guardar.isLoading ? 'Guardando…' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

function Registrados({ campana, onHide }) {
  const { data: registros = [], isLoading } = useQuery(
    ['campana-registros', campana.id],
    async () => (await api.get(`/auth/campanas/${campana.id}/registros/`)).data
  )

  const descargar = async () => {
    try {
      const r = await api.get(`/auth/campanas/${campana.id}/registros/csv/`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `registros-${campana.codigo}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('No se pudo descargar el CSV')
    }
  }

  return (
    <Modal show onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Registrados — {campana.nombre}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Alert variant="light" className="border-0 border-bottom rounded-0 mb-0 small">
          Datos personales: sólo el superadmin los ve, y cada descarga queda en auditoría.
        </Alert>
        {isLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : (
          <Table responsive hover size="sm" className="mb-0 align-middle small">
            <thead className="table-light">
              <tr>
                <th>Nombre</th><th>Correo</th><th>WhatsApp</th><th>Despacho</th><th>RFC</th>
                <th>Lugar</th><th>Verificado</th><th>Licencia</th><th>Acepta contacto</th>
              </tr>
            </thead>
            <tbody>
              {registros.length === 0 && (
                <tr><td colSpan={9} className="text-center text-muted py-4">Nadie se ha registrado.</td></tr>
              )}
              {registros.map((r) => (
                <tr key={r.id}>
                  <td>{r.nombre}</td>
                  <td>{r.email}</td>
                  <td>{r.telefono}</td>
                  <td>{r.despacho}</td>
                  <td>{r.rfc_titular}</td>
                  <td>{r.ciudad}, {r.estado_nombre}</td>
                  <td>{r.verificado_at ? <Badge bg="success">sí</Badge> : <Badge bg="secondary">pendiente</Badge>}</td>
                  <td>
                    {r.licencia === 'en_espera'
                      ? <Badge bg="info">en fila{r.lugar_en_fila ? ` · ${r.lugar_en_fila}` : ''}</Badge>
                      : r.licencia || '—'}
                  </td>
                  <td>{r.acepta_oferta_plan ? 'sí' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={descargar} disabled={!registros.length}>
          Descargar CSV
        </Button>
        <Button onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  )
}
